import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { GetOrCreateVideoControllerParameters } from "../VideoControllerFactory"
import { VideoFlightController } from "../VideoFlightController"
import {
    useGuardedVideoControllerEventBinding,
    useVideoControllerEventBinding,
} from "./useEventBinding"
import { LiveVideoFlightRecordedController } from "../LiveVideoFlightRecordedController"
import { LiveVideoFlightLiveController } from "../LiveVideoFlightLiveController"
import { LiveVideoFlightAnonymousController } from "../LiveVideoFlightAnonymousController"
import { useAppSelector } from "integrations/redux/hooks"
import {
    LiveVideoStatus,
    selectLiveVideoStatusById,
} from "features/entity-caching/flights/selectors"

export function useVideoData(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: [],
        func: (controller) => controller.getData(),
        events: ["dataChanged"],
    })
}

export function useShowTelemetry(params: GetOrCreateVideoControllerParameters) {
    const show = useVideoControllerEventBinding({
        ...params,
        defaultValue: false,
        func: (controller) => (controller as LiveVideoFlightAnonymousController).showTelemetry,
        events: ["dataChanged"],
    })
    return !!params.options?.anonymous && show
}

export function useVideoName(params: GetOrCreateVideoControllerParameters) {
    const name = useVideoControllerEventBinding({
        ...params,
        defaultValue: "",
        func: (controller) => (controller as LiveVideoFlightAnonymousController).streamName,
        events: ["dataChanged"],
    })
    return !!params.options?.anonymous ? name : ""
}

export function useVideoPilotName(params: GetOrCreateVideoControllerParameters) {
    const pilotName = useVideoControllerEventBinding<string | undefined>({
        ...params,
        defaultValue: undefined,
        func: (controller) => (controller as LiveVideoFlightAnonymousController).pilotName,
        events: ["dataChanged"],
    })
    return !!params.options?.anonymous ? pilotName : undefined
}

export function useHasFetchedOriginalData(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: false,
        func: (controller) => controller.hasFetchedOriginalData(),
        events: ["dataChanged"],
    })
}

export function useCanProjectVideo(params: GetOrCreateVideoControllerParameters) {
    return useGuardedVideoControllerEventBinding({
        ...params,
        guardClass: VideoFlightController,
        defaultValue: false,
        func: (controller) => controller.canProject(),
        events: ["dataChanged"],
    })
}

export function useVideoResolutions(params: GetOrCreateVideoControllerParameters) {
    return useGuardedVideoControllerEventBinding({
        ...params,
        guardClass: VideoFlightController,
        defaultValue: [],
        func: (controller) => controller.getResolutions(),
        events: ["dataChanged"],
    })
}

export function useCurrentVideoResolutionIndex(params: GetOrCreateVideoControllerParameters) {
    return useGuardedVideoControllerEventBinding({
        ...params,
        guardClass: VideoFlightController,
        defaultValue: 0,
        func: (controller) => controller.getCurrentResolutionIndex(),
        events: ["videoResolutionIndexChanged"],
    })
}

export function useCurrentFrameIndex(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: 0,
        func: (controller) => controller.getCurrentFrameNumber(),
        events: ["dataChanged", "videoProgressChanged"],
    })
}
export function useVideoCurrentFrameData(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: undefined,
        func: (controller) => controller.getCurrentFrameData(),
        events: ["dataChanged", "videoProgressChanged"],
    })
}

export function useVideoDroneColor(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: droneIconColors[0],
        func: (controller) => controller.getDroneIconColor(),
        events: [],
    })
}

export function useIsPlayingVideo(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: false,
        func: (controller) => controller.isPlayingVideo(),
        events: ["isPlayingChanged"],
    })
}
export function useHasPlayedVideo(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: false,
        func: (controller) => controller.hasPlayedVideo(),
        events: ["isPlayingChanged"],
    })
}

export function useIsWaitingForRecording(params: GetOrCreateVideoControllerParameters) {
    return useGuardedVideoControllerEventBinding({
        ...params,
        guardClass: LiveVideoFlightRecordedController,
        defaultValue: false,
        func: (controller) => controller.isWaitingForRecording(),
        events: ["videoAvailabilityChanged"],
    })
}
export function useIsWaitingForStreamToStart(params: GetOrCreateVideoControllerParameters) {
    return useGuardedVideoControllerEventBinding({
        ...params,
        guardClass: LiveVideoFlightLiveController,
        defaultValue: false,
        func: (controller) => controller.isWaitingForStreamToStart(),
        events: ["videoAvailabilityChanged"],
    })
}

export function useHasAudio(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: false,
        func: (controller) => controller.hasAudio(),
        events: ["audioAvailabilityChanged"],
    })
}

export function useIsAudioMuted(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: true,
        func: (controller) => controller.isAudioMuted(),
        events: ["audioSettingsChanged"],
    })
}

export function useVolumePercent(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: 100,
        func: (controller) => controller.getVolumePercent(),
        events: ["audioSettingsChanged"],
    })
}

export function useIsStreamDisconnected(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: false,
        func: (controller) => controller.isStreamDisconnected(),
        events: ["connectivityChanged"],
    })
}

export function useIsCameraOff(params: GetOrCreateVideoControllerParameters) {
    return useVideoControllerEventBinding({
        ...params,
        defaultValue: false,
        func: (controller) => controller.isCameraOff(),
        events: ["connectivityChanged"],
    })
}

export function useIsStandby({ flightId }: GetOrCreateVideoControllerParameters) {
    return useAppSelector(selectLiveVideoStatusById(flightId)) === LiveVideoStatus.Standby
}

export function useIsAnonymousFlightFinished(params: GetOrCreateVideoControllerParameters) {
    return useGuardedVideoControllerEventBinding({
        ...params,
        guardClass: LiveVideoFlightAnonymousController,
        defaultValue: false,
        func: (controller) => controller.isStreamFinished(),
        events: ["dataChanged"],
    })
}
